import { zodResolver } from "@hookform/resolvers/zod";
import ArchiveIcon from "@mui/icons-material/Archive";
import CampaignIcon from "@mui/icons-material/Campaign";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PublicIcon from "@mui/icons-material/Public";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { LoadingButton } from "@mui/lab";
import { Box, Divider, TextField } from "@mui/material";
import { IResourceComponentsProps } from "@refinedev/core";
import { Edit } from "@refinedev/mui";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { useGetCryptor, usePatchCryptor } from "@/api/cryptor.api";
import UploadField from "@/components/forms/UploadField";
import { removeQueryParams } from "@/utils/parseUrl";
import { getTranslation } from "@/utils/translation";

import CryptorAddressesField from "./components/CryptorAddressesField";
import CryptorArtefactField from "./components/CryptorArtefactField";
import CryptorBirthDateField from "./components/CryptorBirthDateField";
import CryptorBirthDateRawField from "./components/CryptorBirthDateRawField";
import CryptorDeathDateField from "./components/CryptorDeathDateField";
import CryptorDescriptionAIField from "./components/CryptorDescriptionAI";
import CryptorDescriptionField from "./components/CryptorDescriptionField";
import CryptorDifficultyField from "./components/CryptorDifficultyField";
import CryptorLinkWithThePlaceField from "./components/CryptorLinkWithThePlaceField";
import CryptorMistralAgentIdField from "./components/CryptorMistralAgentIdField";
import CryptorNameTextField from "./components/CryptorNameTextField";
import CryptorPlaceOfBirthField from "./components/CryptorPlaceOfBirthField";
import CryptorPrimaryColorField from "./components/CryptorPrimaryColorField";
import CryptorQuoteField from "./components/CryptorQuoteField";
import CryptorRewardField from "./components/CryptorRewardField";
import CryptorShouldAnimateEyesField from "./components/CryptorShouldAnimateEyesField";
import CryptorTitleField from "./components/CryptorTitleField";
import SecondaryColorField from "./components/SecondaryColorField";
import TimeRestrictionField from "./components/TimeRestrictionField";
import UnderMaintenanceField from "./components/UnderMaintenanceField";
import { CryptorFormData, CryptorSchema } from "./utils/cryptor.schema";

export const CryptorEdit: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const methods = useForm<CryptorFormData>({
    resolver: zodResolver(CryptorSchema),
    mode: "onChange",
  });

  // SAFETY: this screen is only mounted from a route that provides a numeric `id` param.
  const { id } = useParams() as unknown as { id: number };
  const { data: cryptor } = useGetCryptor(id);
  const { mutateAsync: patchCryptor } = usePatchCryptor();

  const {
    formState: { isValid },
    setError,
    control,
    handleSubmit,
    reset,
    trigger,
  } = methods;

  useEffect(() => {
    if (!cryptor || !reset) return;
    reset({
      title: getTranslation(cryptor.titleTranslation)?.value || "",
      linkWithThePlace:
        getTranslation(cryptor.linkWithThePlaceTranslation)?.value || "",
      description: getTranslation(cryptor.descriptionTranslation)?.value || "",
      quote: getTranslation(cryptor.quoteTranslation)?.value || "",
      difficulty: getTranslation(cryptor.difficultyTranslation)?.value || "",
      addresses: cryptor.addresses.map((address) => ({
        cityId: address.address.city.id,
        locationAddress: address.address.locationAddress,
        locationRadius: address.address.locationRadius,
        location: {
          latitude: address.address.location.latitude,
          longitude: address.address.location.longitude,
        },
        id: address.address.id,
      })),
      artefactLinkWithThePlace: cryptor.artefactLinkWithThePlace
        ? {
            id: cryptor.artefactLinkWithThePlace.id,
            chatMessageId: Number(
              cryptor.artefactLinkWithThePlace.chatMessageId,
            ),
            image: { url: cryptor.artefactLinkWithThePlace.image.url },
            titlePopUp:
              getTranslation(
                cryptor.artefactLinkWithThePlace.titlePopUpTranslation,
              )?.value || "",
            textPopUp:
              getTranslation(
                cryptor.artefactLinkWithThePlace.textPopUpTranslation,
              )?.value || "",
            reward: Number(cryptor.artefactLinkWithThePlace.reward),
          }
        : null,
      artefactDescription: cryptor.artefactDescription
        ? {
            id: cryptor.artefactDescription.id,
            chatMessageId: Number(cryptor.artefactDescription.chatMessageId),
            image: { url: cryptor.artefactDescription.image.url },
            titlePopUp:
              getTranslation(cryptor.artefactDescription.titlePopUpTranslation)
                ?.value || "",
            textPopUp:
              getTranslation(cryptor.artefactDescription.textPopUpTranslation)
                ?.value || "",
            reward: Number(cryptor.artefactDescription.reward),
          }
        : null,
      artefactQuote: cryptor.artefactQuote
        ? {
            id: cryptor.artefactQuote.id,
            chatMessageId: Number(cryptor.artefactQuote.chatMessageId),
            image: { url: cryptor.artefactQuote.image.url },
            titlePopUp:
              getTranslation(cryptor.artefactQuote.titlePopUpTranslation)
                ?.value || "",
            textPopUp:
              getTranslation(cryptor.artefactQuote.textPopUpTranslation)
                ?.value || "",
            reward: Number(cryptor.artefactQuote.reward),
          }
        : null,
      archived: cryptor.archived,
      draft: cryptor.draft,
      descriptionForAI: cryptor.descriptionForAI || undefined,
      birthDate: cryptor.birthDate ? new Date(cryptor.birthDate) : null,
      birthDateRaw: cryptor.birthDateRaw || undefined,
      deathDate: cryptor.deathDate ? new Date(cryptor.deathDate) : null,
      placeOfBirth: cryptor.placeOfBirth,
      avatar: { image: { url: cryptor.avatar.image.url } },
      characterImage: { url: cryptor.characterImage.url },
      primaryColor: cryptor.primaryColor,
      secondaryColor: cryptor.secondaryColor,
      timeRestriction: cryptor.timeRestriction,
      timeRestrictionUrl: cryptor.timeRestrictionUrl || undefined,
      underMaintenance: cryptor.underMaintenance,
      image: { url: cryptor.image.url },
      mapIcon: { url: cryptor.mapIcon?.url ?? null },
      obfuscatedImage: { url: cryptor.obfuscatedImage.url },
      name: cryptor.name,
      reward: cryptor.reward,
      mistralAIAgentIdFR: cryptor.mistralAIAgentIdFR || undefined,
      mistralAIAgentIdEN: cryptor.mistralAIAgentIdEN || undefined,
      mistralAIAgentIdES: cryptor.mistralAIAgentIdES || undefined,
      mistralAIAgentIdDE: cryptor.mistralAIAgentIdDE || undefined,
      mistralAIAgentIdHU: cryptor.mistralAIAgentIdHU || undefined,
      mistralAIAgentIdIT: cryptor.mistralAIAgentIdIT || undefined,
      mistralAIAgentIdNL: cryptor.mistralAIAgentIdNL || undefined,
      mistralAIAgentIdPL: cryptor.mistralAIAgentIdPL || undefined,
      mistralAIAgentIdPT: cryptor.mistralAIAgentIdPT || undefined,
      shouldAnimateEyes: cryptor.shouldAnimateEyes,
    });
    void trigger();
  }, [reset, trigger, cryptor]);

  const onFinishHandler = async (data: CryptorFormData) => {
    await patchCryptor({
      id,
      name: data.name,
      reward: data.reward,
      primaryColor: data.primaryColor,
      secondaryColor: data.secondaryColor,
      timeRestriction: data.timeRestriction,
      timeRestrictionUrl: data.timeRestrictionUrl || null,
      underMaintenance: data.underMaintenance,
      image: { url: removeQueryParams(data.image.url) },
      mapIcon: data.mapIcon?.url
        ? { url: removeQueryParams(data.mapIcon.url) }
        : null,
      obfuscatedImage: { url: removeQueryParams(data.obfuscatedImage.url) },
      avatar: { image: { url: removeQueryParams(data.avatar.image.url) } },
      characterImage: { url: removeQueryParams(data.characterImage.url) },
      addresses: data.addresses.map((address) => ({
        locationAddress: address.locationAddress,
        cityId: address.cityId,
        location: {
          latitude: Number(address.location?.latitude),
          longitude: Number(address.location?.longitude),
        },
        locationRadius: Number(address.locationRadius),
      })),
      descriptionForAI: data.descriptionForAI,
      // SAFETY: the backend accepts the serialized date value produced by the form layer.
      birthDate: data.birthDate as unknown as string,
      birthDateRaw: data.birthDateRaw || null,
      // SAFETY: the backend accepts the serialized date value produced by the form layer.
      deathDate: data.deathDate as unknown as string,
      placeOfBirth: data.placeOfBirth,
      translationTitle: { value: data.title },
      translationLinkWithThePlace: { value: data.linkWithThePlace },
      translationDescription: { value: data.description },
      translationQuote: { value: data.quote },
      translationDifficulty: { value: data.difficulty },
      artefactLinkWithThePlace: data.artefactLinkWithThePlace
        ? {
            id: data.artefactLinkWithThePlace.id,
            image: { url: data.artefactLinkWithThePlace.image.url },
            titlePopUpTranslation: {
              value: data.artefactLinkWithThePlace.titlePopUp,
            },
            textPopUpTranslation: {
              value: data.artefactLinkWithThePlace.textPopUp,
            },
            reward: data.artefactLinkWithThePlace.reward,
            chatMessageId: Number(data.artefactLinkWithThePlace.chatMessageId),
          }
        : null,
      artefactDescription: data.artefactDescription
        ? {
            id: data.artefactDescription.id,
            image: { url: data.artefactDescription.image.url },
            titlePopUpTranslation: {
              value: data.artefactDescription.titlePopUp,
            },
            textPopUpTranslation: { value: data.artefactDescription.textPopUp },
            reward: data.artefactDescription.reward,
            chatMessageId: Number(data.artefactDescription.chatMessageId),
          }
        : null,
      artefactQuote: data.artefactQuote
        ? {
            id: data.artefactQuote.id,
            image: { url: data.artefactQuote.image.url },
            titlePopUpTranslation: {
              value: data.artefactQuote.titlePopUp,
            },
            textPopUpTranslation: { value: data.artefactQuote.textPopUp },
            reward: data.artefactQuote.reward,
            chatMessageId: Number(data.artefactQuote.chatMessageId),
          }
        : null,
      mistralAIAgentIdFR: data.mistralAIAgentIdFR || null,
      mistralAIAgentIdEN: data.mistralAIAgentIdEN || null,
      mistralAIAgentIdES: data.mistralAIAgentIdES || null,
      mistralAIAgentIdDE: data.mistralAIAgentIdDE || null,
      mistralAIAgentIdHU: data.mistralAIAgentIdHU || null,
      mistralAIAgentIdIT: data.mistralAIAgentIdIT || null,
      mistralAIAgentIdNL: data.mistralAIAgentIdNL || null,
      mistralAIAgentIdPL: data.mistralAIAgentIdPL || null,
      mistralAIAgentIdPT: data.mistralAIAgentIdPT || null,
      shouldAnimateEyes: !!data.shouldAnimateEyes,
    });

    navigate("/cryptors");
  };

  const isArchived = useMemo(() => cryptor?.archived, [cryptor]);

  const isDraft = useMemo(() => cryptor?.draft, [cryptor]);

  const onArchiveHandler = (archived: boolean) => {
    patchCryptor({ id, archived });
  };

  const onDraftHandler = (draft: boolean) => {
    patchCryptor({ id, draft });
  };

  return (
    <Edit
      saveButtonProps={{
        onClick: handleSubmit(onFinishHandler),
        disabled: !isValid,
      }}
      headerButtons={[
        <LoadingButton
          key="questions"
          onClick={() => navigate(`/questions/edit?cryptorId=${id}`)}
        >
          <Box display="flex" gap={0.5}>
            <PersonSearchIcon />
            Questions post capture
          </Box>
        </LoadingButton>,
        <LoadingButton
          key="hints"
          onClick={() => {
            navigate(`/hints/edit?cryptorId=${id}`);
            // It reloads the page and prevents the page to not rerender properly. A fix is needed.
            navigate(0);
          }}
        >
          <Box display="flex" gap={0.5}>
            <PersonSearchIcon />
            Indices
          </Box>
        </LoadingButton>,
        <LoadingButton
          key="advertisements"
          onClick={() =>
            navigate(`/cryptors/advertisements/edit?cryptorId=${id}`)
          }
        >
          <Box display="flex" gap={0.5}>
            <CampaignIcon />
            Publicités
          </Box>
        </LoadingButton>,
        <Divider key="divider" orientation="vertical" flexItem />,
        <LoadingButton
          key="archive"
          onClick={handleSubmit(() => onArchiveHandler(!isArchived))}
          startIcon={isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
        >
          {isArchived ? "Désarchiver" : "Archiver"}
        </LoadingButton>,
        <LoadingButton
          key="draft"
          onClick={handleSubmit(() => onDraftHandler(!isDraft))}
          startIcon={isDraft ? <PublicIcon /> : <PublicOffIcon />}
        >
          {isDraft ? "publier" : "dépublier"}
        </LoadingButton>,
      ]}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column" }}
          autoComplete="off"
        >
          <TextField
            value={cryptor?.id ?? id}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="number"
            label="Id"
            disabled
          />
          <CryptorNameTextField control={control} />
          {!methods.watch("birthDateRaw") && (
            <CryptorBirthDateField control={control} />
          )}
          {!methods.watch("birthDate") && (
            <CryptorBirthDateRawField control={control} />
          )}
          <CryptorDeathDateField control={control} />
          <CryptorPlaceOfBirthField control={control} />
          <CryptorTitleField control={control} />
          <CryptorLinkWithThePlaceField control={control} />
          <CryptorQuoteField control={control} />
          <CryptorDescriptionField control={control} />
          <CryptorDifficultyField control={control} />
          <CryptorRewardField control={control} />
          <CryptorPrimaryColorField control={control} />
          <SecondaryColorField control={control} />
          <TimeRestrictionField control={control} />
          <UnderMaintenanceField control={control} />

          <CryptorArtefactField
            cryptorId={cryptor?.id}
            artefactName="artefactLinkWithThePlace"
            title="Artefact (lien avec le lieu)"
          />
          <CryptorArtefactField
            cryptorId={cryptor?.id}
            artefactName="artefactDescription"
            title="Artefact (description)"
          />
          <CryptorArtefactField
            cryptorId={cryptor?.id}
            artefactName="artefactQuote"
            title="Artefact (citation)"
          />

          <CryptorAddressesField />
          <UploadField
            name="image.url"
            uploadButtonLabel="Upload image"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
          />
          <UploadField
            name="mapIcon.url"
            uploadButtonLabel="Upload icône carte"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
            optional
          />
          <UploadField
            name="obfuscatedImage.url"
            uploadButtonLabel="Upload image (dissimulée)"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
          />
          <UploadField
            name="avatar.image.url"
            uploadButtonLabel="Upload avatar"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
          />
          <UploadField
            name="characterImage.url"
            uploadButtonLabel="Upload image personnage"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
          />
          <CryptorDescriptionAIField control={control} />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdFR"
            label="Mistral Agent ID FR"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdEN"
            label="Mistral Agent ID EN"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdES"
            label="Mistral Agent ID ES"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdDE"
            label="Mistral Agent ID DE"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdHU"
            label="Mistral Agent ID HU"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdIT"
            label="Mistral Agent ID IT"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdNL"
            label="Mistral Agent ID NL"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdPL"
            label="Mistral Agent ID PL"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdPT"
            label="Mistral Agent ID PT"
          />
          <CryptorShouldAnimateEyesField control={control} />
        </Box>
      </FormProvider>
    </Edit>
  );
};
